import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LOGO, NavBar, FB_URL, IG_URL, WHATSAPP, RECEPTION_WHATSAPP, BANK, ADDRESS } from "../shared";


// ─── CONFIG ────────────────────────────────────────────────────────────────────
const BANK_DETAILS = BANK;


const FEATURES = [
  { icon: "🛡️", label: "Fully Insured" },
  { icon: "👨‍✈️", label: "Professional Driver" },
  { icon: "❄️", label: "Air Conditioned" },
  { icon: "🛞", label: "Well Maintained" },
  { icon: "📍", label: "GPS Tracked" },
  { icon: "🔇", label: "Quiet & Private" },
];

const USES = [
  { icon: "✈️", label: "Airport Transfers" },
  { icon: "🏢", label: "Business Trips" },
  { icon: "💍", label: "Weddings & Events" },
  { icon: "🏖️", label: "Leisure & Tourism" },
  { icon: "🏥", label: "Medical Trips" },
  { icon: "🎓", label: "School Runs" },
];

function buildWhatsApp({ ref, form }) {
  const days = form.returnDate
    ? Math.max(1, Math.ceil((new Date(form.returnDate) - new Date(form.pickupDate)) / 86400000))
    : 1;

  return `🚗 *NEW CAR HIRE REQUEST — THE 401 HOTEL & SUITES LTD*
━━━━━━━━━━━━━━━━━━
🔖 Booking Ref: *${ref}*
📅 Date: ${new Date().toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
━━━━━━━━━━━━━━━━━━
🚐 *VEHICLE:* Toyota Sienna (7-Seater)
👨‍✈️ *Driver:* Included
━━━━━━━━━━━━━━━━━━
📋 *TRIP DETAILS:*
  Pickup Date: ${form.pickupDate ? new Date(form.pickupDate).toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) : "TBC"}
  Pickup Time: ${form.pickupTime || "TBC"}
  Return Date: ${form.returnDate ? new Date(form.returnDate).toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) : "Same day"}
  Duration: ${days} day${days > 1 ? "s" : ""}
  Pickup Location: ${form.pickupLocation || "TBC"}
  Destination: ${form.destination || "TBC"}
  Purpose: ${form.purpose || "Not specified"}
━━━━━━━━━━━━━━━━━━
💰 *PAYMENT:* ${form.payment}
  (Full payment required to confirm booking)
━━━━━━━━━━━━━━━━━━
👤 *CUSTOMER DETAILS:*
  Name: ${form.name}
  Phone: ${form.phone}
  Special Requests: ${form.notes || "None"}
━━━━━━━━━━━━━━━━━━
Please confirm availability & quote the current rate.
Reply CONFIRMED once payment received.
⚡ We beat any ongoing market price!`;
}

export default function CarHire() {
  const navigate = useNavigate();
  const [page, setPage] = useState("home"); // home | booking | confirm
  const [form, setForm] = useState({
    name: "", phone: "", pickupDate: "", pickupTime: "", returnDate: "",
    pickupLocation: "", destination: "", purpose: "", payment: "", notes: "",
  });
  const [sending, setSending] = useState(false);
  const [ref] = useState(() => "401C-" + Math.floor(1000 + Math.random() * 9000));

  const today = new Date().toISOString().split("T")[0];

  const send = () => {
    setSending(true);
    const msg = buildWhatsApp({ ref, form });
    setTimeout(() => {
      window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`, "_blank");
      setSending(false);
      setPage("confirm");
    }, 800);
  };

  // ── Colors ────────────────────────────────────────────────────────────────
  const C = {
    bg: "#07090e", surface: "#0c0f18", card: "#131826", border: "#1c2438",
    gold: "#c8a040", goldLight: "#e8c060", cream: "#eee4cc",
    muted: "#657090", green: "#3aad72", red: "#e03a2e",
  };

  const goldBtn = {
    display: "block", width: "100%",
    background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
    border: "none", borderRadius: 12, padding: "15px",
    fontSize: 15, fontWeight: "bold", color: C.bg,
    cursor: "pointer", letterSpacing: 1, fontFamily: "inherit", marginTop: 16,
  };
  const inp = {
    width: "100%", background: C.card, border: `1px solid ${C.border}`,
    borderRadius: 10, padding: "12px 14px", color: C.cream,
    fontSize: 14, fontFamily: "inherit", marginBottom: 14,
    boxSizing: "border-box", outline: "none", colorScheme: "dark",
  };
  const lbl = { display: "block", fontSize: 10, letterSpacing: 2, color: C.gold, marginBottom: 6 };

  // ── HOME ──────────────────────────────────────────────────────────────────
  const renderHome = () => (
    <div>
      {/* Hero */}
      <div style={{
        background: "linear-gradient(160deg, #0d1020 0%, #07090e 100%)",
        padding: "40px 22px 32px", textAlign: "center",
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{
          display: "inline-block", border: `1px solid ${C.gold}55`,
          color: C.gold, padding: "3px 16px", borderRadius: 20,
          fontSize: 10, letterSpacing: 3, marginBottom: 18,
        }}>THE 401 HOTEL & SUITES LTD · AWKA</div>

        <div style={{ fontSize: 56, marginBottom: 12 }}>🚐</div>

        <h1 style={{
          fontSize: 32, fontWeight: "bold", color: C.cream,
          margin: "0 0 10px", lineHeight: 1.1, letterSpacing: 1,
        }}>401 Car Hire</h1>

        <p style={{ color: C.gold, fontSize: 14, margin: "0 0 6px", fontStyle: "italic" }}>
          Quiet. Private. Professional. Serenity.
        </p>
        <p style={{ color: C.muted, fontSize: 12, margin: "0 0 28px", letterSpacing: 1 }}>
          We beat any ongoing market price
        </p>

        {/* Price enquiry badge */}
        <div style={{
          background: `${C.gold}15`, border: `1px solid ${C.gold}44`,
          borderRadius: 14, padding: "14px 20px", marginBottom: 24,
        }}>
          <div style={{ fontSize: 11, color: C.gold, letterSpacing: 2, marginBottom: 6 }}>PRICING</div>
          <div style={{ color: C.cream, fontSize: 14, lineHeight: 1.7 }}>
            Our rates are adjusted to match the current market.<br />
            <span style={{ color: C.gold, fontWeight: "bold" }}>We will always beat any competitor's price.</span>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>
            Contact us for today's rate before booking.
          </div>
          <a href={`https://wa.me/${WHATSAPP}?text=Hello! Please send me the current Toyota Sienna car hire rate for The 401 Hotel.`}
            target="_blank" rel="noreferrer"
            style={{
              display: "inline-block", marginTop: 10,
              background: "#25D366", borderRadius: 8, padding: "8px 18px",
              color: "#fff", fontSize: 12, fontWeight: "bold",
              textDecoration: "none", fontFamily: "inherit",
            }}>
            📲 Ask for Today's Rate
          </a>
        </div>

        <button style={goldBtn} onClick={() => setPage("booking")}>
          Book the Sienna →
        </button>
      </div>

      {/* Vehicle Card */}
      <div style={{ padding: "20px 18px" }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 16 }}>OUR VEHICLE</div>
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: 18, overflow: "hidden",
        }}>
          <div style={{
            background: "linear-gradient(135deg, #1a2030, #0d1020)",
            padding: "24px 20px", textAlign: "center",
            borderBottom: `1px solid ${C.border}`,
          }}>
            <div style={{ fontSize: 72, marginBottom: 12 }}>🚐</div>
            <div style={{ fontSize: 20, fontWeight: "bold", color: C.cream, marginBottom: 4 }}>
              Toyota Sienna
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              Premium 7-Seater MPV · Air Conditioned
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
              {[["7", "Seats"], ["A/C", "Climate"], ["Pro", "Driver"]].map(([val, lbl]) => (
                <div key={lbl} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: "bold", color: C.gold }}>{val}</div>
                  <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>{lbl.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div style={{ padding: "16px 20px" }}>
            <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 12 }}>INCLUDED</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {FEATURES.map((f) => (
                <div key={f.label} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: C.surface, borderRadius: 10, padding: "10px 12px",
                  border: `1px solid ${C.border}`,
                }}>
                  <span style={{ fontSize: 18 }}>{f.icon}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Use cases */}
      <div style={{ padding: "0 18px 20px" }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 16 }}>PERFECT FOR</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          {USES.map((u) => (
            <div key={u.label} style={{
              background: C.card, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: "14px 10px", textAlign: "center",
            }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{u.icon}</div>
              <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>{u.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Payment policy */}
      <div style={{ padding: "0 18px 20px" }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 12 }}>BOOKING POLICY</div>
          {[
            ["💰", "Full payment required to confirm your booking"],
            ["🏦", "Pay via POS at the hotel or bank transfer (Zenith Bank)"],
            ["📲", "Confirmation sent via WhatsApp once payment received"],
            ["⚡", "We guarantee to beat any ongoing market rate"],
          ].map(([icon, text]) => (
            <div key={text} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
              <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Social footer */}
      <div style={{ padding: "16px 20px 28px", borderTop: `1px solid ${C.border}`, textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 12 }}>
          <a href="https://instagram.com/the401hotel" target="_blank" rel="noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 6, color: C.gold, fontSize: 12, textDecoration: "none", fontFamily: "inherit" }}>
            <span>📸</span> @the401hotel
          </a>
          <a href="https://www.facebook.com/share/1EJKkvzsNL/" target="_blank" rel="noreferrer"
            style={{ display: "flex", alignItems: "center", gap: 6, color: C.gold, fontSize: 12, textDecoration: "none", fontFamily: "inherit" }}>
            <span>📘</span> The 401 Hotel
          </a>
        </div>
        <p style={{ color: C.muted, fontSize: 10, letterSpacing: 1, lineHeight: 1.8, margin: 0 }}>
          Plot A/39 Iyiagu Estate, Off Abakaliki Street<br />
          Awka, Anambra State · the401homes.com
        </p>
      </div>
    </div>
  );

  // ── BOOKING FORM ──────────────────────────────────────────────────────────
  const renderBooking = () => (
    <div style={{ padding: "20px 18px" }}>
      <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 6 }}>CAR HIRE BOOKING</div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>Toyota Sienna · Driver Included</div>

      {/* Rate reminder */}
      <div style={{
        background: `${C.gold}12`, border: `1px solid ${C.gold}33`,
        borderRadius: 12, padding: "12px 16px", marginBottom: 20,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <span style={{ fontSize: 20 }}>⚡</span>
        <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
          Not sure of the current rate?{" "}
          <a href={`https://wa.me/${WHATSAPP}?text=Hi! Please quote me the current Toyota Sienna hire rate.`}
            target="_blank" rel="noreferrer"
            style={{ color: C.gold, textDecoration: "none", fontWeight: "bold" }}>
            Ask us on WhatsApp first →
          </a>
        </span>
      </div>

      <label style={lbl}>YOUR FULL NAME</label>
      <input style={inp} placeholder="As on your ID" value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })} />

      <label style={lbl}>PHONE NUMBER</label>
      <input style={inp} placeholder="08XXXXXXXXX" value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })} />

      <label style={lbl}>PICKUP DATE</label>
      <input type="date" style={inp} min={today} value={form.pickupDate}
        onChange={(e) => setForm({ ...form, pickupDate: e.target.value })} />

      <label style={lbl}>PICKUP TIME</label>
      <input type="time" style={inp} value={form.pickupTime}
        onChange={(e) => setForm({ ...form, pickupTime: e.target.value })} />

      <label style={lbl}>RETURN DATE (leave blank if same day)</label>
      <input type="date" style={inp} min={form.pickupDate || today} value={form.returnDate}
        onChange={(e) => setForm({ ...form, returnDate: e.target.value })} />

      <label style={lbl}>PICKUP LOCATION</label>
      <input style={inp} placeholder="e.g. The 401 Hotel, Iyiagu Estate" value={form.pickupLocation}
        onChange={(e) => setForm({ ...form, pickupLocation: e.target.value })} />

      <label style={lbl}>DESTINATION</label>
      <input style={inp} placeholder="e.g. Enugu Airport, Lagos, Onitsha..." value={form.destination}
        onChange={(e) => setForm({ ...form, destination: e.target.value })} />

      <label style={lbl}>PURPOSE OF TRIP</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {["Airport Transfer", "Business Trip", "Wedding / Event", "Leisure / Tourism", "Medical", "Other"].map((opt) => (
          <button key={opt} onClick={() => setForm({ ...form, purpose: opt })}
            style={{
              background: form.purpose === opt ? C.gold : C.card,
              border: `1px solid ${form.purpose === opt ? C.gold : C.border}`,
              borderRadius: 8, padding: "8px 12px",
              color: form.purpose === opt ? C.bg : C.muted,
              fontSize: 12, cursor: "pointer", fontFamily: "inherit",
              fontWeight: form.purpose === opt ? "bold" : "normal",
            }}>{opt}</button>
        ))}
      </div>

      <label style={lbl}>PAYMENT METHOD</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        {["Bank Transfer (Zenith Bank)", "POS at Hotel"].map((opt) => (
          <button key={opt} onClick={() => setForm({ ...form, payment: opt })}
            style={{
              background: form.payment === opt ? `${C.gold}18` : C.card,
              border: `1px solid ${form.payment === opt ? C.gold : C.border}`,
              borderRadius: 10, padding: "12px 14px",
              color: form.payment === opt ? C.gold : C.muted,
              fontSize: 13, cursor: "pointer", fontFamily: "inherit",
              textAlign: "left", fontWeight: form.payment === opt ? "bold" : "normal",
            }}>{form.payment === opt ? "✓ " : ""}{opt}</button>
        ))}
      </div>

      {form.payment === "Bank Transfer (Zenith Bank)" && (
        <div style={{
          background: C.card, border: `1px solid ${C.gold}33`,
          borderRadius: 12, padding: "14px", marginBottom: 16,
        }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: C.gold, marginBottom: 8 }}>BANK DETAILS</div>
          <div style={{ fontSize: 13, color: C.cream, lineHeight: 2, whiteSpace: "pre-line" }}>{BANK_DETAILS}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            Send payment proof on WhatsApp to confirm booking
          </div>
        </div>
      )}

      <label style={lbl}>SPECIAL REQUESTS / NOTES (OPTIONAL)</label>
      <textarea style={{ ...inp, minHeight: 72, resize: "vertical" }}
        placeholder="Any stops, extra needs, or special requirements..."
        value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

      {/* Policy box */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 14, padding: "16px 18px", marginBottom: 4,
      }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 12 }}>BOOKING POLICY</div>
        {[
          ["💰", "Full payment required to confirm your booking"],
          ["🏦", "Pay via POS at the hotel or bank transfer (Zenith Bank)"],
          ["📲", "We will confirm your booking via WhatsApp once payment received"],
          ["⚡", "We guarantee to beat any ongoing market rate"],
        ].map(([icon, text]) => (
          <div key={text} style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "flex-start" }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
            <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{text}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => {
          if (!form.name || !form.phone || !form.payment) {
            return alert("Please fill in your name, phone number and select a payment method.");
          }
          if (!form.pickupDate) return alert("Please select a pickup date.");
          send();
        }}
        style={{
          display: "block", width: "100%",
          background: sending ? "#1a8a40" : "#25D366",
          border: "none", borderRadius: 12, padding: "16px",
          fontSize: 15, fontWeight: "bold", color: "#fff",
          cursor: "pointer", letterSpacing: 0.5, fontFamily: "inherit",
        }}>
        {sending ? "⏳ Opening WhatsApp..." : "📲 Book via WhatsApp"}
      </button>
      <p style={{ textAlign: "center", fontSize: 10, color: C.muted, marginTop: 10, letterSpacing: 1 }}>
        Your booking goes directly to our team on WhatsApp
      </p>
    </div>
  );

  // ── CONFIRM ───────────────────────────────────────────────────────────────
  const renderConfirm = () => (
    <div style={{ padding: 32, textAlign: "center" }}>
      <div style={{ fontSize: 64, marginBottom: 12 }}>🚐</div>
      <div style={{ fontSize: 28, fontWeight: "bold", color: C.gold, marginBottom: 8 }}>Booking Sent!</div>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: 3, marginBottom: 12 }}>BOOKING REFERENCE</div>
      <div style={{
        background: C.card, border: `1px solid ${C.gold}44`,
        borderRadius: 14, padding: "14px 28px",
        fontSize: 22, fontWeight: "bold", color: C.gold,
        letterSpacing: 4, display: "inline-block", marginBottom: 20,
      }}>{ref}</div>

      <div style={{
        background: "#25D36618", border: "1px solid #25D36644",
        borderRadius: 14, padding: "16px 18px", marginBottom: 20,
      }}>
        <div style={{ fontSize: 22, marginBottom: 6 }}>📲</div>
        <div style={{ color: "#25D366", fontWeight: "bold", fontSize: 14, marginBottom: 6 }}>
          Booking request sent to our team
        </div>
        <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.6 }}>
          We will confirm availability, quote your current rate, and confirm once payment is received.
        </div>
      </div>

      {/* Summary */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 14, padding: 18, textAlign: "left", marginBottom: 20,
      }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 12 }}>WHAT HAPPENS NEXT</div>
        {[
          ["1️⃣", "Our team confirms vehicle availability"],
          ["2️⃣", "We send you the current best rate on WhatsApp"],
          ["3️⃣", "Make payment via POS or bank transfer"],
          ["4️⃣", "Send payment proof — booking confirmed ✅"],
          ["5️⃣", "Driver arrives at your location on time"],
        ].map(([num, step]) => (
          <div key={num} style={{ display: "flex", gap: 10, marginBottom: 10, fontSize: 13 }}>
            <span>{num}</span>
            <span style={{ color: C.muted }}>{step}</span>
          </div>
        ))}
      </div>

      {/* Trip details */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 14, padding: 18, textAlign: "left", marginBottom: 20,
      }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: C.gold, marginBottom: 12 }}>YOUR BOOKING DETAILS</div>
        {[
          ["Vehicle", "Toyota Sienna (Driver Included)"],
          ["Name", form.name],
          ["Phone", form.phone],
          ["Pickup", form.pickupDate ? new Date(form.pickupDate).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" }) : "TBC"],
          ["Time", form.pickupTime || "TBC"],
          ["From", form.pickupLocation || "TBC"],
          ["To", form.destination || "TBC"],
          ["Payment", form.payment],
        ].filter(([, v]) => v && v !== "TBC").map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 7 }}>
            <span style={{ color: C.muted }}>{k}</span>
            <span style={{ color: C.cream, fontWeight: "500", maxWidth: "60%", textAlign: "right" }}>{v}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => {
          setPage("home");
          setForm({ name: "", phone: "", pickupDate: "", pickupTime: "", returnDate: "", pickupLocation: "", destination: "", purpose: "", payment: "", notes: "" });
        }}
        style={{
          background: "transparent", border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 24px", color: C.muted,
          cursor: "pointer", fontFamily: "inherit", fontSize: 13,
        }}>
        ← Make Another Booking
      </button>
    </div>
  );

  // ── LAYOUT ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      fontFamily: "'Palatino Linotype','Book Antiqua',Palatino,Georgia,serif",
      background: C.bg, minHeight: "100vh", color: C.cream,
      maxWidth: "100%", margin: "0 auto",
    }}>
      <NavBar title="Car Hire" />
      <style>{`@media(min-width:768px){.pb{max-width:720px;margin:0 auto}.cr{display:grid!important;grid-template-columns:repeat(auto-fill,minmax(170px,1fr))!important}}`}</style>
      {/* Header */}
      <div style={{
        background: C.surface, borderBottom: `1px solid ${C.border}`,
        padding: "14px 18px", display: "flex", alignItems: "center",
        justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100,
      }}>
        <div>
          <img src={LOGO} style={{height:36,objectFit:"contain",display:"block",marginBottom:2}} alt="The 401 Hotel & Suites Ltd" />
          <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>
            Toyota Sienna · Driver Included · Best Rate Guaranteed
          </div>
        </div>
        {page !== "home" && page !== "confirm" && (
          <button onClick={() => setPage("home")}
            style={{
              background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: 8, padding: "7px 12px", color: C.muted,
              cursor: "pointer", fontSize: 12, fontFamily: "inherit",
            }}>← Back</button>
        )}
      </div>

      {/* Pages */}
      <div style={{ paddingBottom: 40 }}>
        {page === "home" && renderHome()}
        {page === "booking" && renderBooking()}
        {page === "confirm" && renderConfirm()}
      </div>
    </div>
  );
}
